import React from "react";
import { trackEvent } from "@leafly-com/web-utils";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";

import getSeo from "api/requests/directus/getSeo";
import { getProductMarketplaceCategories } from "api/requests/getProductMarketplaceCategories";
import getSponsoredArticles, {
  SponsoredArticle,
} from "api/requests/getSponsoredArticles";
import publicConfig from "config/public";
import { SEO } from "custom-types/Directus";
import { ProductMarketplaceCategory } from "custom-types/Product";
import { getCountryCodeHeader } from "utils/requestContext";

import ArticleCard from "components/botanic/ArticleCard";
import Carousel from "components/botanic/Carousel";
import FAQs from "components/FAQ";
import GoogleAd from "components/GoogleAd";
import { FeaturedProducts } from "components/products/FeaturedProducts";
import { HeroBanner } from "components/products/HeroBanner";
import { LazyLoadProductsCarouselWithData } from "components/products/LazyLoadProductsCarouselWithData";
import { hasIosParam } from "components/products/lib/paramCheck";
import { ProductCategoryGoogleAdTargeting } from "components/products/ProductCategoryGoogleAdTargeting";
import { ProductCategoryMetaTags } from "components/products/ProductCategoryMetaTags";
import { ProductCategoryNavigation } from "components/products/ProductCategoryNavigation";
import { ProductsCarouselWithData } from "components/products/ProductsCarouselWithData";
import ProductsCopyBlocks from "components/products/ProductsCopyBlocks";
import TrackImpression from "components/TrackImpression";

export const CAROUSEL_ROUTE_MAP = {
  cartridges: "/products/concentrates/cartridges",
  concentrates: "/products/concentrates",
  edibles: "/products/edibles",
  flowers: "/products/cannabis/flower",
  prerolls: "/products/cannabis/prerolls",
  vapes: "/products/vaping",
} as const;

const DEFAULT_H1 = "Shop Cannabis Products &amp; Accessories Online";
const DEFAULT_DESCRIPTION =
  "Buy all types of cannabis and CBD products, plus deals available in your area.";
const DEFAULT_IMAGE = `${publicConfig.imgix.publicUrl}/products/category-heroes/product-heroes-v4-1920x500-all-products-1x.jpg`;

const ProductIndex: NextPage<{
  categories: ProductMarketplaceCategory[];
  articles: SponsoredArticle[];
  seo?: SEO;
}> = ({ categories, articles, seo }) => {
  const router = useRouter();
  const isIos = hasIosParam(router);

  return (
    <>
      <ProductCategoryGoogleAdTargeting category="home" />

      <ProductCategoryMetaTags
        title={seo?.metaTitle}
        description={seo?.metaDescription}
        image={seo?.heroImage}
        canonicalOverride={seo?.canonicalOverride}
      />

      <ProductCategoryNavigation categories={categories} />

      <HeroBanner
        imageSrc={seo?.heroImage || DEFAULT_IMAGE}
        title={seo?.h1 || DEFAULT_H1}
        subtitle={seo?.description || DEFAULT_DESCRIPTION}
      />

      <div className="container pb-section" data-testid="home-container">
        <GoogleAd
          adPath="/13212770/product/category_hero"
          className="pt-lg"
          sizes={{ desktop: ["fluid"], mobile: ["fluid"] }}
          style={{ minHeight: 188 }}
        />

        <FeaturedProducts className="my-section" titleOverride={seo?.h2} />

        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.flowers}
          header="Flower"
          filter={{ subcategory: "flower" }}
        />

        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.edibles}
          header="Edibles"
          filter={{ category: "edibles" }}
        />

        <GoogleAd
          adPath="/13212770/product/product_upper"
          className="mt-section"
          sizes={{ desktop: [728, 90] }}
        />

        <GoogleAd
          adPath="/13212770/product/product_lower"
          className="mt-section"
          sizes={{ mobile: [320, 50] }}
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.cartridges}
            header="Cartridges"
            filter={{ subcategory: "cartridges" }}
          />
        )}

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.prerolls}
          header="Pre-rolls"
          filter={{ subcategory: "prerolls" }}
        />

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.concentrates}
          header="Concentrates"
          filter={{ category: "concentrates" }}
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.vapes}
            header="Vaping"
            filter={{ category: "vaping" }}
          />
        )}

        <ProductsCopyBlocks blocks={seo?.copyBlocks} />

        <FAQs
          title={
            seo?.faqTitle ||
            "Frequently asked questions about buying marijuana products"
          }
          data={seo?.faqs}
          initialShowPanel={true}
          className="mt-section"
        />

        <GoogleAd
          adPath="/13212770/product/product_mid"
          className="mt-section"
          sizes={{ mobile: [300, 250] }}
        />

        {!!articles?.length && (
          <Carousel className="mt-section" data-testid="articles">
            {articles.map((article) => {
              const {
                sponsorName,
                excerpt,
                date,
                link,
                image,
                title,
                tags,
                slug,
              } = article;

              return (
                <TrackImpression
                  category="Product Article"
                  label={slug}
                  key={date}
                >
                  <ArticleCard
                    key={date}
                    className="mb-md"
                    onClick={() => trackEvent("Product Article", "Click", slug)}
                    author={`Sponsored by ${sponsorName}`}
                    featured={true}
                    excerpt={excerpt}
                    href={link}
                    tags={tags}
                    title={title}
                    imageUrl={image}
                    sizes={[750]}
                  />
                </TrackImpression>
              );
            })}
          </Carousel>
        )}
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const countryCode = getCountryCodeHeader(context.req.headers);

  const [{ categories }, articles, seo] = await Promise.all([
    getProductMarketplaceCategories(countryCode),
    getSponsoredArticles({ id: 0, sponsorType: "category" }),
    getSeo(context.resolvedUrl),
  ]);

  return {
    props: {
      articles,
      categories,
      seo,
    },
  };
};

export default ProductIndex;
